/**
 * React Query hooks for the shared radiation module (fieldiq-api#1334, RAD-1).
 * One key family, `['radiation', …]`, so any write refreshes the register, the
 * source page, the summary and dosimetry together. Bodies are read through
 * lib/radiation.ts; every response's `meta` (units, zone, today) rides along.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import { unwrap, unwrapArray } from '../lib/api-envelope';
import { LOAD_FAILURE_QUERY_OPTIONS } from '../lib/load-failure';
import {
  readCatalog,
  readDecay,
  readDose,
  readMeta,
  readSettings,
  readSource,
  readSourceDetail,
  readStatus,
} from '../lib/radiation';
import type {
  RadDose,
  RadDoseWrite,
  RadEventWrite,
  RadLeakTestWrite,
  RadSettingsWrite,
  RadSource,
  RadSourceWrite,
} from '../types/radiation';

export const RAD_KEY = ['radiation'] as const;

const metaOf = (body: any) => readMeta(body?.meta);

export function useRadCatalog(enabled = true) {
  return useQuery({
    queryKey: [...RAD_KEY, 'catalog'],
    queryFn: async () => readCatalog(unwrap<any>((await api.radiation.catalog()).data)),
    staleTime: 60 * 60 * 1000,
    enabled,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function useRadSettings(enabled = true) {
  return useQuery({
    queryKey: [...RAD_KEY, 'settings'],
    queryFn: async () => {
      const body = (await api.radiation.settings()).data as any;
      return { settings: readSettings(unwrap<any>(body)), meta: metaOf(body) };
    },
    enabled,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function useRadStatus(enabled = true) {
  return useQuery({
    queryKey: [...RAD_KEY, 'status'],
    queryFn: async () => {
      const body = (await api.radiation.status()).data as any;
      return { status: readStatus(unwrap<any>(body)), meta: metaOf(body) };
    },
    enabled,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function useRadSources(enabled = true) {
  return useQuery({
    queryKey: [...RAD_KEY, 'sources'],
    queryFn: async () => {
      const body = (await api.radiation.sources()).data as any;
      return {
        rows: unwrapArray<any>(body).map(readSource).filter((s): s is RadSource => s !== null),
        meta: metaOf(body),
      };
    },
    enabled,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function useRadSource(id: string | undefined) {
  return useQuery({
    queryKey: [...RAD_KEY, 'source', id],
    queryFn: async () => {
      const body = (await api.radiation.source(id!)).data as any;
      return { source: readSourceDetail(unwrap<any>(body)), meta: metaOf(body) };
    },
    enabled: !!id,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

/** Decay to a chosen day. `on` null = no request (the page shows today's figure from the source). */
export function useRadDecay(id: string | undefined, on: string | null) {
  return useQuery({
    queryKey: [...RAD_KEY, 'decay', id, on],
    queryFn: async () => readDecay(unwrap<any>((await api.radiation.decay(id!, on!)).data)),
    enabled: !!id && !!on,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function useRadDosimetry(enabled = true) {
  return useQuery({
    queryKey: [...RAD_KEY, 'dosimetry'],
    queryFn: async () => {
      const body = (await api.radiation.dosimetry()).data as any;
      return { rows: unwrapArray<any>(body).map(readDose).filter((d): d is RadDose => d !== null), meta: metaOf(body) };
    },
    enabled,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function useInvalidateRad() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: RAD_KEY });
}

const useRadMutation = <B, R>(fn: (body: B) => Promise<R>) => {
  const invalidate = useInvalidateRad();
  return useMutation({ mutationFn: fn, onSuccess: invalidate });
};

export const useUpdateRadSettings = () =>
  useRadMutation(async (body: RadSettingsWrite) => readSettings(unwrap<any>((await api.radiation.updateSettings(body)).data)));

export const useCreateRadSource = () =>
  useRadMutation(async (body: RadSourceWrite) => readSource(unwrap<any>((await api.radiation.createSource(body)).data)));

export const useUpdateRadSource = (id: string) =>
  useRadMutation(async (body: RadSourceWrite) => readSource(unwrap<any>((await api.radiation.updateSource(id, body)).data)));

export const useDeleteRadSource = (id: string) =>
  useRadMutation(async (_: void) => (await api.radiation.deleteSource(id)).data);

export const useRecordRadLeakTest = (id: string) =>
  useRadMutation(async (body: RadLeakTestWrite) => (await api.radiation.recordLeakTest(id, body)).data as any);

/** Returns the whole envelope: `notified` (lost in hole) is a sibling of `data`. */
export const useRecordRadEvent = (id: string) =>
  useRadMutation(async (body: RadEventWrite) => (await api.radiation.recordEvent(id, body)).data as any);

export const useCreateRadDose = () =>
  useRadMutation(async (body: RadDoseWrite) => readDose(unwrap<any>((await api.radiation.createDose(body)).data)));

export const useUpdateRadDose = () =>
  useRadMutation(async ({ id, body }: { id: string; body: RadDoseWrite }) =>
    readDose(unwrap<any>((await api.radiation.updateDose(id, body)).data)));

export const useDeleteRadDose = () =>
  useRadMutation(async (id: string) => (await api.radiation.deleteDose(id)).data);
